import React, { useEffect, useRef, useState } from "react";
import { Bell, AlertTriangle, CheckCircle2, Inbox, LifeBuoy, ShieldAlert } from "lucide-react";
import api from "@/lib/api";
import { pushBrowserNotification } from "@/lib/notify";

/**
 * Notification bell for the dashboard header.
 * Polls for Dox's completed work and shows an unread badge + panel.
 * Fires a real browser notification the first time a new item appears.
 */
export default function NotificationBell({ bump = 0, onNavigateDoc }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const seenIds = useRef(new Set());
  const primed = useRef(false);

  const load = async (firePush = true) => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.notifications);
      setUnread(data.unread);
      if (firePush && primed.current) {
        data.notifications
          .filter((n) => !n.read && !seenIds.current.has(n.id))
          .forEach((n) => pushBrowserNotification(n.title, n.body));
      }
      data.notifications.forEach((n) => seenIds.current.add(n.id));
      primed.current = true;
    } catch { /* ignore */ }
  };

  useEffect(() => { load(false); }, []); // eslint-disable-line
  useEffect(() => { if (bump) load(true); }, [bump]); // eslint-disable-line
  useEffect(() => {
    const id = setInterval(() => load(true), 15000);
    const onFocus = () => load(true);
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, []); // eslint-disable-line

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load(false);
  };
  const openItem = async (n) => {
    await api.post(`/notifications/${n.id}/read`);
    setOpen(false);
    load(false);
    if (n.doc_id && onNavigateDoc) onNavigateDoc(n.doc_id);
  };

  return (
    <div className="relative">
      <button
        data-testid="notification-bell"
        onClick={() => setOpen((v) => !v)}
        className="relative h-9 w-9 rounded-full glass flex items-center justify-center text-white/70 hover:text-white transition-colors"
      >
        <Bell size={17} />
        {unread > 0 && (
          <span data-testid="notification-badge" className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-dox-orange text-[10px] font-bold flex items-center justify-center text-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-80 z-50 glass-strong rounded-2xl border border-white/10 overflow-hidden" data-testid="notification-panel">
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <span className="font-display font-bold text-sm">Dox updates</span>
              {unread > 0 && <button onClick={markAll} className="text-xs text-dox-orange hover:underline" data-testid="notif-mark-all">Mark all read</button>}
            </div>
            <div className="max-h-96 overflow-y-auto">
              {items.length === 0 ? (
                <div className="py-10 text-center text-white/40 text-sm flex flex-col items-center gap-2">
                  <Inbox size={22} /> Nothing yet. Hand Dox some work.
                </div>
              ) : items.map((n) => (
                <button
                  key={n.id}
                  data-testid={`notif-${n.id}`}
                  onClick={() => openItem(n)}
                  className={`w-full text-left flex gap-3 px-4 py-3 border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors ${!n.read ? "bg-white/[0.03]" : ""}`}
                >
                  <span className={`mt-0.5 shrink-0 ${n.type === "action_required" ? "text-amber-400" : n.type === "needs_attention" ? "text-amber-400" : n.type === "verification" ? "text-yellow-400" : "text-emerald-400"}`}>
                    {n.type === "action_required" ? <AlertTriangle size={16} /> : n.type === "needs_attention" ? <LifeBuoy size={16} /> : n.type === "verification" ? <ShieldAlert size={16} /> : <CheckCircle2 size={16} />}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-white">{n.title}</span>
                    <span className="block text-xs text-white/50 mt-0.5">{n.body}</span>
                  </span>
                  {!n.read && <span className="ml-auto mt-1 h-2 w-2 rounded-full bg-dox-orange shrink-0" />}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
